/**
 * GuardianCalculator — the pricing model the user pitches to the manager.
 *
 *   STRUCTURES (radio):
 *     1. Watch + On-Call          platform × N + ad-hoc hours × $200
 *     2. Portfolio Retainer       platform × N + pooled committed × $110   ← recommended
 *     3. Per-Company Bundles      bundleCos × $3,225 + rest × platform + ad-hoc hours × $200
 *
 *   MANAGER ECONOMICS (radio):
 *     - Pass-through           manager funds expert/retainer; companies pay platform
 *     - Wholesale + markup     manager buys platform @ wholesale, sells @ retail
 *     - Referral share         manager earns % of platform revenue
 *
 * Legal is an explicit $0 line — quoted separately by CompliSun Legal, PLC;
 * never enters manager earnings (MRPC 5.4 / 1.8(f)).
 */
import { useState } from "react";
import { Calculator, Lock, Sparkles } from "lucide-react";
import { PRICING, computeEconomics, fmt } from "@/lib/guardianData";

const STRUCTURES = [
  { id: "watch_oncall",       label: "Watch + On-Call",         hint: "Platform every co + ad-hoc hours at $200 — lightest." },
  { id: "portfolio_retainer", label: "Portfolio Retainer",      hint: "Platform every co + a shared committed-hours pool at $110 — recommended.", recommended: true },
  { id: "per_company_bundle", label: "Per-Company Bundles",     hint: "Active bundle ($3,225) for companies needing more; rest on platform — expansion ramp." },
];

const MANAGER_MODELS = [
  { id: "passthrough",       label: "Pass-through",       hint: "Companies pay platform; manager funds the expert/retainer side." },
  { id: "wholesale_markup",  label: "Wholesale + markup", hint: "Manager buys platform wholesale and resells at retail (keeps the spread)." },
  { id: "referral_share",    label: "Referral share",     hint: "Manager earns % of platform revenue only — no legal share." },
];

export default function GuardianCalculator({ companies }) {
  const N = companies.length;
  const [structure, setStructure] = useState("portfolio_retainer");
  const [adHocHours, setAdHocHours] = useState(20);
  const [pooledHours, setPooledHours] = useState(40);
  const [bundleCos, setBundleCos] = useState(Math.max(1, Math.round(N / 3)));
  const [volumeDiscount, setVolumeDiscount] = useState(true);
  const [installEnabled, setInstallEnabled] = useState(false);
  const [installCos, setInstallCos] = useState(N);
  const [managerModel, setManagerModel] = useState("passthrough");
  const [wholesale, setWholesale] = useState(PRICING.wholesalePerCoDefault);
  const [retail, setRetail] = useState(1000);
  const [referralPct, setReferralPct] = useState(PRICING.referralPctDefault);

  const econ = computeEconomics({
    structure, N,
    adHocHours, pooledHours, bundleCos,
    volumeDiscount, installEnabled, installCos,
    managerModel,
    wholesalePerCo: wholesale, retailPerCo: retail, referralPct,
  });

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ PRICING CALCULATOR · MODEL THE ECONOMICS</div>
        <h2 className="text-2xl font-semibold flex items-center gap-2" style={{ color: "#0F1E2E" }}><Calculator className="h-5 w-5" style={{ color: "#F5B400" }} /> {N} companies · live</h2>
        <p className="text-sm text-ink-900/65 mt-1 max-w-3xl">
          Per-tenant + expert hours. <strong>No per-user, no per-seat.</strong> Manager margin derives only from platform + compliance. Legal is a separate add-on, not part of the platform.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        {/* Inputs column */}
        <div className="space-y-5">
          {/* Structure */}
          <div>
            <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ STRUCTURE</div>
            <div className="space-y-2">
              {STRUCTURES.map((s) => (
                <label key={s.id} className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition"
                       style={{ background: structure === s.id ? "rgba(245,180,0,0.10)" : "white", borderColor: structure === s.id ? "#F5B400" : "rgba(15,30,46,0.10)" }}>
                  <input type="radio" name="structure" value={s.id} checked={structure === s.id} onChange={() => setStructure(s.id)}
                         data-testid={`calc-struct-${s.id}`} className="mt-1" style={{ accentColor: "#F5B400" }} />
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2" style={{ color: "#0F1E2E" }}>
                      {s.label}
                      {s.recommended && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#F5B400", color: "#0F1E2E", fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}><Sparkles className="h-2 w-2 inline mb-0.5" /> RECOMMENDED</span>}
                    </div>
                    <div className="text-xs text-ink-900/60 mt-0.5">{s.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Hours input — context-aware */}
          <div className="space-y-3">
            {structure === "watch_oncall" && (
              <NumberInput label="Ad-hoc hours / month (@ $200)" value={adHocHours} onChange={setAdHocHours} testId="calc-adhoc" min={0} max={400} />
            )}
            {structure === "portfolio_retainer" && (
              <NumberInput label="Pooled committed hours / month (@ $110)" value={pooledHours} onChange={setPooledHours} testId="calc-pooled" min={0} max={400} />
            )}
            {structure === "per_company_bundle" && (
              <>
                <NumberInput label={`Companies on Active bundle (max ${N})`} value={bundleCos} onChange={(v) => setBundleCos(Math.min(N, Math.max(0, v)))} testId="calc-bundle-cos" min={0} max={N} />
                <NumberInput label="Ad-hoc hours / month for the rest (@ $200)" value={adHocHours} onChange={setAdHocHours} testId="calc-adhoc2" min={0} max={400} />
              </>
            )}
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <Toggle label={`Volume discount (1–10=$1,000 · 11–25=$850 · 26+=$750) — at N=${N} → $${econ.ppc.toLocaleString()}/co`} value={volumeDiscount} onChange={setVolumeDiscount} testId="calc-volume" />
            <Toggle label="Include one-time install" value={installEnabled} onChange={setInstallEnabled} testId="calc-install" />
            {installEnabled && (
              <NumberInput label={`Companies installing (@ $${PRICING.installPerCo.toLocaleString()})`} value={installCos} onChange={(v) => setInstallCos(Math.min(N, Math.max(0, v)))} min={0} max={N} testId="calc-install-cos" />
            )}
          </div>

          {/* Manager model */}
          <div>
            <div className="text-[10px] mb-2" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>/ MANAGER ECONOMICS</div>
            <div className="space-y-2">
              {MANAGER_MODELS.map((m) => (
                <label key={m.id} className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition"
                       style={{ background: managerModel === m.id ? "rgba(245,180,0,0.10)" : "white", borderColor: managerModel === m.id ? "#F5B400" : "rgba(15,30,46,0.10)" }}>
                  <input type="radio" name="mgr-model" value={m.id} checked={managerModel === m.id} onChange={() => setManagerModel(m.id)}
                         data-testid={`calc-mgr-${m.id}`} className="mt-1" style={{ accentColor: "#F5B400" }} />
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "#0F1E2E" }}>{m.label}</div>
                    <div className="text-xs text-ink-900/60 mt-0.5">{m.hint}</div>
                  </div>
                </label>
              ))}
            </div>
            {managerModel === "wholesale_markup" && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <NumberInput label="Wholesale / co / mo" value={wholesale} onChange={setWholesale} min={0} max={2000} prefix="$" testId="calc-wholesale" />
                <NumberInput label="Retail to companies / co / mo" value={retail} onChange={setRetail} min={0} max={5000} prefix="$" testId="calc-retail" />
              </div>
            )}
            {managerModel === "referral_share" && (
              <div className="mt-3">
                <NumberInput label="Referral share (%)" value={referralPct} onChange={setReferralPct} min={0} max={50} suffix="%" testId="calc-referral" />
              </div>
            )}
          </div>
        </div>

        {/* Outputs column */}
        <div className="space-y-3">
          <Output title="Companies' total spend" mo={econ.companiesSpendMonthly} arr={econ.companiesSpendARR} testId="out-cos" />
          <Output
            title={econ.managerLabel}
            mo={managerModel === "passthrough" ? econ.managerNetCostMonthly : econ.managerEarningsMonthly}
            arr={managerModel === "passthrough" ? econ.managerNetCostARR : econ.managerEarningsARR}
            tone={managerModel === "passthrough" ? "neutral" : "good"}
            testId="out-mgr"
          />
          <Output title="CompliSun, Inc. revenue (platform + compliance)" mo={econ.csMonthly} arr={econ.csARR} tone="brand" testId="out-cs" />
          {econ.installRevenueOnce > 0 && (
            <div className="rounded-md p-3 border" style={{ background: "white", borderColor: "rgba(15,30,46,0.08)" }}>
              <div className="text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>ONE-TIME INSTALL</div>
              <div className="text-lg font-bold" style={{ color: "#0F1E2E" }}>{fmt.money(econ.installRevenueOnce)}</div>
            </div>
          )}

          {/* Legal — explicit $0 line, walled off */}
          <div className="rounded-md p-3 border-2 border-dashed" style={{ borderColor: "rgba(15,30,46,0.20)", background: "rgba(15,30,46,0.02)" }} data-testid="out-legal">
            <div className="flex items-center gap-2 text-[10px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.55)" }}>
              <Lock className="h-3 w-3" /> LEGAL · ADD-ON (NOT IN PLATFORM)
            </div>
            <div className="text-lg font-bold" style={{ color: "rgba(15,30,46,0.65)" }}>$0</div>
            <p className="text-[11px] text-ink-900/60 mt-1 leading-relaxed">
              Legal is an optional add-on quoted separately when a company needs counsel (retainer, hourly, fractional GC, or fractional team). It never enters manager earnings.
            </p>
          </div>

          <div className="rounded-md p-4" style={{ background: "#0F1E2E", color: "#FDFBF5" }} data-testid="out-relarr">
            <div className="text-[10px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(245,180,0,0.85)" }}>TOTAL RELATIONSHIP ARR (excl. legal)</div>
            <div className="text-3xl font-bold" style={{ color: "#F5B400", fontFamily: "ui-monospace, SF Mono, monospace" }}>{fmt.money(econ.relationshipARR)}</div>
            <div className="text-[11px] mt-1 opacity-70">{N} companies · {STRUCTURES.find((s) => s.id === structure)?.label}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, min = 0, max = 1000, prefix, suffix, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.55)" }}>{label.toUpperCase()}</div>
      <div className="flex items-center border rounded-md" style={{ borderColor: "rgba(15,30,46,0.15)" }}>
        {prefix && <span className="px-2 text-sm text-ink-900/60">{prefix}</span>}
        <input
          type="number"
          value={value}
          min={min} max={max}
          onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
          data-testid={testId}
          className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent"
        />
        {suffix && <span className="px-2 text-sm text-ink-900/60">{suffix}</span>}
      </div>
    </label>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} style={{ accentColor: "#F5B400" }} />
      <span style={{ color: "#0F1E2E" }}>{label}</span>
    </label>
  );
}

function Output({ title, mo, arr, tone = "neutral", testId }) {
  const bg = tone === "brand" ? "rgba(245,180,0,0.10)" : tone === "good" ? "rgba(22,163,74,0.08)" : "white";
  const fg = tone === "brand" ? "#C98F00" : tone === "good" ? "#15803d" : "#0F1E2E";
  return (
    <div className="rounded-md p-3 border" style={{ background: bg, borderColor: tone === "brand" ? "rgba(245,180,0,0.30)" : "rgba(15,30,46,0.08)" }} data-testid={testId}>
      <div className="text-[10px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.5)" }}>{title.toUpperCase()}</div>
      <div className="flex items-baseline gap-3 mt-0.5">
        <div className="text-xl font-bold" style={{ color: fg, fontFamily: "ui-monospace, SF Mono, monospace" }}>{fmt.money(mo)}<span className="text-xs font-normal opacity-65">/mo</span></div>
        <div className="text-sm opacity-70" style={{ color: fg, fontFamily: "ui-monospace, SF Mono, monospace" }}>{fmt.money(arr)} / yr</div>
      </div>
    </div>
  );
}
